import React, { useState, useEffect, useRef } from 'react';
import { useImportStore } from '../../stores/importStore';
import { useSettingsStore } from '../../stores/settingsStore';

function ImportTab() {
  const { status, progress, summary, startImport } = useImportStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (files && files.length > 0) {
      startImport(files);
    }
    // Reset input so same files can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const isImporting = status === 'importing';

  return (
    <div className="import-tab">
      <h2>Import Hand Histories</h2>
      <p className="import-desc">
        Import PokerStars-format hand history files (.txt). Only showdown hands with the winner's cards present are added to the training pool. Select as many files as you want — large batches are uploaded automatically in chunks.
      </p>

      <div className="import-actions">
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="file-upload"
        />
        <button
          className="btn-import"
          onClick={() => fileInputRef.current?.click()}
          disabled={isImporting}
        >
          Select Files (.txt)
        </button>
      </div>

      {/* Drop zone */}
      <div
        className="import-dropzone"
        onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
        onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
        onDrop={(e) => {
          e.preventDefault();
          e.currentTarget.classList.remove('dragover');
          if (e.dataTransfer.files.length > 0 && !isImporting) {
            startImport(e.dataTransfer.files);
          }
        }}
      >
        Or drag and drop .txt files here
      </div>

      {/* Progress */}
      {(isImporting || status === 'complete' || status === 'error') && (
        <div className="import-progress-section">
          {isImporting && (
            <>
              <div className="import-status-label">Importing...</div>
              {progress && (
                <>
                  <div className="progress-bar-track">
                    <div
                      className="progress-bar-fill"
                      style={{ width: progress.filesTotal > 0 ? `${Math.min(100, (progress.filesProcessed / progress.filesTotal) * 100)}%` : '0%' }}
                    />
                  </div>
                  <div className="progress-stats">
                    <span>Parsed: {progress.handsParsed.toLocaleString()}</span>
                    <span>Added: {progress.handsAdded.toLocaleString()}</span>
                    <span>Errors: {progress.handsError.toLocaleString()}</span>
                  </div>
                  {progress.currentFile && (
                    <div className="current-file">Processing: {progress.currentFile}</div>
                  )}
                </>
              )}
            </>
          )}

          {status === 'complete' && summary && (
            <div className="import-summary">
              <h3>Import Complete</h3>
              <div className="summary-grid">
                <div className="summary-item success">
                  <div className="summary-num">{summary.handsAdded.toLocaleString()}</div>
                  <div className="summary-label">Hands Added</div>
                </div>
                <div className="summary-item">
                  <div className="summary-num">{summary.handsFound.toLocaleString()}</div>
                  <div className="summary-label">Total Found</div>
                </div>
                <div className="summary-item warn">
                  <div className="summary-num">{summary.handsDuplicate.toLocaleString()}</div>
                  <div className="summary-label">Duplicates Skipped</div>
                </div>
                <div className="summary-item warn">
                  <div className="summary-num">{summary.handsError.toLocaleString()}</div>
                  <div className="summary-label">Parse Errors</div>
                </div>
              </div>

              {summary.handsSkippedByRule && Object.keys(summary.handsSkippedByRule).length > 0 && (
                <div className="eligibility-breakdown">
                  <h4>Skipped by Eligibility Rule</h4>
                  {Object.entries(summary.handsSkippedByRule).map(([reason, count]) => (
                    <div key={reason} className="eligibility-row">
                      <span>{formatSkipReason(reason)}</span>
                      <span>{(count as number).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {status === 'error' && (
            <div className="import-error">Import failed. Check file format and try again.</div>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const { settings, update, load } = useSettingsStore();
  const [colorScheme, setColorScheme] = useState(settings?.colorScheme ?? 'standard');
  const [anonymizeDefault, setAnonymizeDefault] = useState(settings?.anonymizeDefault ?? false);
  const [matrixDefault, setMatrixDefault] = useState<'full' | 'blank'>(settings?.matrixDefault ?? 'full');
  const [dataSourceDefault, setDataSourceDefault] = useState<'default' | 'user' | 'both'>(settings?.dataSourceDefault ?? 'both');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (settings) {
      setColorScheme(settings.colorScheme ?? 'standard');
      setAnonymizeDefault(settings.anonymizeDefault ?? false);
      setMatrixDefault(settings.matrixDefault ?? 'full');
      setDataSourceDefault(settings.dataSourceDefault ?? 'both');
    }
  }, [settings]);

  async function handleSave() {
    await update({ colorScheme, anonymizeDefault, matrixDefault, dataSourceDefault });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="settings-tab">
      <h2>Settings</h2>

      <div className="settings-section">
        <h3>Display</h3>
        <div className="setting-row">
          <label>Suit Color Scheme</label>
          <div className="radio-group">
            <label className="radio-label">
              <input type="radio" name="colorScheme" value="standard" checked={colorScheme === 'standard'} onChange={() => setColorScheme('standard')} />
              Standard (red/black)
            </label>
            <label className="radio-label">
              <input type="radio" name="colorScheme" value="colorblind" checked={colorScheme === 'colorblind'} onChange={() => setColorScheme('colorblind')} />
              Colorblind-friendly (blue/orange)
            </label>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Session Defaults</h3>
        <div className="setting-row">
          <label>Anonymize Players by Default</label>
          <input type="checkbox" checked={anonymizeDefault} onChange={e => setAnonymizeDefault(e.target.checked)} />
        </div>
        <div className="setting-row">
          <label>Matrix Starting State</label>
          <div className="radio-group">
            <label className="radio-label">
              <input type="radio" name="matrixDefault" value="full" checked={matrixDefault === 'full'} onChange={() => setMatrixDefault('full')} />
              Full range (all 169 highlighted)
            </label>
            <label className="radio-label">
              <input type="radio" name="matrixDefault" value="blank" checked={matrixDefault === 'blank'} onChange={() => setMatrixDefault('blank')} />
              Blank (add hands manually)
            </label>
          </div>
        </div>
        <div className="setting-row">
          <label>Default Data Source</label>
          <select className="filter-select" value={dataSourceDefault} onChange={e => setDataSourceDefault(e.target.value as 'default' | 'user' | 'both')}>
            <option value="both">Both</option>
            <option value="default">Default DB</option>
            <option value="user">My Hands</option>
          </select>
        </div>
      </div>

      <button className="btn-save-settings" onClick={handleSave}>
        {saved ? 'Saved' : 'Save Settings'}
      </button>
    </div>
  );
}

export function ImportSettingsScreen() {
  const [activeTab, setActiveTab] = useState<'import' | 'settings'>('import');

  return (
    <div className="import-settings-screen">
      <div className="tab-bar">
        <button className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`} onClick={() => setActiveTab('import')}>Import</button>
        <button className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>Settings</button>
      </div>
      <div className="tab-content">
        {activeTab === 'import' ? <ImportTab /> : <SettingsTab />}
      </div>
    </div>
  );
}

function formatSkipReason(reason: string): string {
  const labels: Record<string, string> = {
    noShowdown: 'No showdown data',
    noWinnerCards: "Winner's cards absent",
    splitPot: 'Split pot',
    missingStacks: 'Missing stack sizes',
    missingPositions: 'Positions unknown',
    tooManyPlayers: 'More than 9 players',
    runItMultiple: 'Run-it-multiple excluded',
  };
  return labels[reason] ?? reason;
}
